import React, { useEffect, useState, useCallback } from 'react';
import { defineMessages, useIntl, FormattedMessage } from 'react-intl-next';

import { Box, Inline, Text } from '@atlaskit/primitives';

import { GeneratingStreamAnimation } from '@atlassian/ai-components';

export type AudioTimeIndicatorVariants = 'default' | 'blue';

type AudioTimeIndicatorProps = {
	audioRef: React.RefObject<HTMLAudioElement>;
	isLoading: boolean;
	variant?: AudioTimeIndicatorVariants;
};

type TextWrapperProps = {
	children: React.ReactNode;
	variant?: AudioTimeIndicatorVariants;
};

const DEFAULT_TIME_DISPLAY = '0:00';

// Extra padding ensures there is no spacing change when transitioning to/from the generating animation
const PaddedBox = ({ children }: { children: React.ReactNode }) => (
	<Box paddingBlock="space.025">{children}</Box>
);

const i18n = defineMessages({
	generatingAudioAnimationAlt: {
		id: 'audio.audio-time-indicator.generating-alt',
		defaultMessage: 'Generating',
		description: 'Alt text of the generation animation',
	},
	loadingStateMessage: {
		id: 'audio.audio-time-indicator.loading-state-message',
		defaultMessage: 'Generating briefing...',
		description:
			'The message in our audio time indicator when we are generating the audio briefing, and do not have time information to show.',
	},
});

const formatTime = (timeInSeconds: number): string => {
	const minutes = Math.floor(timeInSeconds / 60);
	const seconds = Math.floor(timeInSeconds % 60);
	return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

const TextWrapper = ({ children, variant }: TextWrapperProps) => {
	if (variant === 'blue') {
		return (
			<Text color="color.text.selected" weight="medium">
				{children}
			</Text>
		);
	}

	return (
		<Text color="color.text.subtle" weight="regular">
			{children}
		</Text>
	);
};

const DividerWrapper = ({ children, variant }: TextWrapperProps) => {
	if (variant === 'blue') {
		return (
			<Text color="color.text.disabled" weight="semibold">
				{children}
			</Text>
		);
	}

	return (
		<Text color="color.text.subtlest" weight="medium">
			{children}
		</Text>
	);
};

const LoadingState = () => {
	return (
		<PaddedBox>
			<Text color="color.text.subtle">
				<FormattedMessage {...i18n.loadingStateMessage} />
			</Text>
		</PaddedBox>
	);
};

export const AudioTimeIndicator = ({
	audioRef,
	isLoading,
	variant = 'default',
}: AudioTimeIndicatorProps) => {
	const { formatMessage } = useIntl();

	const [currentTimeDisplay, setCurrentTimeDisplay] = useState(DEFAULT_TIME_DISPLAY);
	const [totalTimeDisplay, setTotalTimeDisplay] = useState(DEFAULT_TIME_DISPLAY);

	const handleTimeUpdate = useCallback(() => {
		const audioEl = audioRef.current;
		if (!audioEl) return;

		const currentTime = Math.floor(audioEl.currentTime ?? 0);
		setCurrentTimeDisplay(formatTime(currentTime));
	}, [audioRef]);

	const handleDurationChange = useCallback(() => {
		const audioEl = audioRef.current;
		if (!audioEl) return;

		if (audioEl.duration !== Infinity) {
			setTotalTimeDisplay(formatTime(audioEl.duration));
		} else {
			setTotalTimeDisplay(DEFAULT_TIME_DISPLAY);
		}
	}, [audioRef]);

	useEffect(() => {
		const audioEl = audioRef.current;
		if (!audioEl) {
			setCurrentTimeDisplay(DEFAULT_TIME_DISPLAY);
			setTotalTimeDisplay(DEFAULT_TIME_DISPLAY);
			return;
		}

		audioEl.addEventListener('durationchange', handleDurationChange);
		audioEl.addEventListener('timeupdate', handleTimeUpdate);

		return () => {
			audioEl.removeEventListener('durationchange', handleDurationChange);
			audioEl.removeEventListener('timeupdate', handleTimeUpdate);
		};
	}, [audioRef, handleTimeUpdate, handleDurationChange]);

	const isAudioDurationUnknown = isLoading || totalTimeDisplay === DEFAULT_TIME_DISPLAY;

	if (isLoading) {
		return <LoadingState />;
	}

	return (
		<Box testId="audio-time-indicator">
			<Inline alignInline="center" space="space.100">
				<PaddedBox>
					<TextWrapper variant={variant}>
						{isLoading ? DEFAULT_TIME_DISPLAY : currentTimeDisplay}
					</TextWrapper>
				</PaddedBox>
				<PaddedBox>
					<DividerWrapper variant={variant}>/</DividerWrapper>
				</PaddedBox>
				{isAudioDurationUnknown ? (
					<GeneratingStreamAnimation alt={formatMessage(i18n.generatingAudioAnimationAlt)} />
				) : (
					<PaddedBox>
						<TextWrapper variant={variant}>{totalTimeDisplay}</TextWrapper>
					</PaddedBox>
				)}
			</Inline>
		</Box>
	);
};
